import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injector } from '@angular/core';
import { Subject, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { ApiService } from '../core/api.service';
import { Base64Service } from '../core/base64.service';
import { DirtyOutService } from '../core/crypt/dirty-out.service';
import { SyncCryptService } from '../core/crypt/sync-crypt.service';
import { SyncDigestService } from '../core/crypt/sync-digest.service';
import { LoggerService } from '../core/logger.service';
import { SyncCookieService } from '../core/sync-cookie.service';
import { UrlService } from '../core/url.service';
import { UserService } from '../core/user.service';
import { LinkFileListService } from '../link-consume/services/link-file-list.service';
import {
  ERROR_CODES,
  BaseApiInput,
  BlendEvent,
  CONSTANTS,
  DirtyOutEncResultSync,
  ErrCode,
  FeatureFlags,
} from '../shared/models';
import { FinishBackupApiInput } from '../shared/models/api/finishbackup-api.model';
import { ITransferItemUpload, ITransferItemStats, IUploadPayload } from 'src/sync';
import { BlendService } from '../shared/services/blend.service';
import * as crc32 from 'crc-32';
import {
  BUNDLE_FILE_THRESHOLD,
  CHUNKSIZE,
  GCM_CHUNK_OVERHEAD_BYTES,
  MAX_RETRY_PER_UPLOAD_HOST,
  MAX_SIZE_HEIC_THUMB,
  MAX_SIZE_IMG_THUMB,
  MAX_SIZE_READ_MEMORY,
  MULTI_CHUNK_UPLOAD_TIMEOUT_MS,
} from './transfer.constants';
import { IBundleItem, IBundleUploadResult, TRANSFER_STATUS } from './models';

enum TransferItemProgressType {
  CRYPT,
  NETWORK,
}
export class FileUploader {
  private syncDigestService: SyncDigestService;
  private loggerService: LoggerService;
  private apiService: ApiService;
  private userService: UserService;
  private syncCryptService: SyncCryptService;
  private urlService: UrlService;
  private http: HttpClient;
  private dirtyOutService: DirtyOutService;
  private base64Service: Base64Service;
  private linkPathListService: LinkFileListService;
  private syncCookieService: SyncCookieService;
  private blendService: BlendService;

  constructor(private injector: Injector) {
    this.syncDigestService = this.injector.get(SyncDigestService);
    this.loggerService = this.injector.get(LoggerService);
    this.apiService = this.injector.get(ApiService);
    this.userService = this.injector.get(UserService);
    this.syncCryptService = this.injector.get(SyncCryptService);
    this.urlService = this.injector.get(UrlService);
    this.http = this.injector.get(HttpClient);
    this.dirtyOutService = this.injector.get(DirtyOutService);
    this.base64Service = this.injector.get(Base64Service);
    this.linkPathListService = this.injector.get(LinkFileListService);
    this.syncCookieService = this.injector.get(SyncCookieService);
    this.blendService = this.injector.get(BlendService);
  }

  public initSha1Digest(tItem: ITransferItemUpload): ITransferItemUpload {
    tItem.sha1_digest = this.syncDigestService.init();
    return tItem;
  }

  /**
   * Prepares a small file for bundled upload without actually uploading.
   * Reads file, initializes SHA1 digest, encrypts data, and calculates CRC32.
   * Used by the batch upload flow for small files ONLY (< BUNDLE_FILE_THRESHOLD).
   * Large files must use uploadItem() which handles chunking.
   */
  public async prepareFileForBundle(tItem: ITransferItemUpload): Promise<IBundleItem> {
    // Safety check: large files must NOT go through bundled upload
    if (tItem.filesize >= BUNDLE_FILE_THRESHOLD) {
      this.loggerService.error(
        `prepareFileForBundle called with large file (${tItem.filesize} bytes). ` +
          `Files >= ${BUNDLE_FILE_THRESHOLD} bytes must use uploadItem() for proper chunking.`,
      );
      throw new Error(`File too large for bundling: ${tItem.filesize} bytes`);
    }

    // Read file into memory
    const buffer = await this.readFileAsArrayBuffer(tItem.fileobj);
    const bytearray = this.syncCryptService.arraybufferToBytes(buffer);

    // Initialize and update SHA1 digest
    tItem.sha1_digest = this.syncDigestService.init();
    tItem.sha1_digest = this.syncDigestService.update(tItem.sha1_digest, bytearray);

    // Encrypt the data
    const encByteArray = await this.syncCryptService.filedataEncrypt(bytearray, tItem.data_key, 0);

    // Calculate CRC32 of encrypted data
    const crc32Hex = this.getCrc32Hex(encByteArray);

    return {
      tItem,
      encryptedData: new Uint8Array(encByteArray),
      crc32: crc32Hex,
      encryptedSize: encByteArray.length,
    };
  }

  /**
   * Reads a File object as ArrayBuffer.
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt: any): void => {
        resolve(evt.target.result as ArrayBuffer);
      };
      reader.onerror = (): void => {
        this.loggerService.error('Error reading file for bundle');
        reject({ errcode: 7010 });
      };
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Uploads multiple prepared bundle items in a single uploadmultichunk request.
   * Returns array of results with blob_id and cachekey for each item.
   * Includes retry logic with host cycling (same as uploadMultiChunk).
   */
  public async uploadBundledChunks(items: IBundleItem[], retryAttempt = 0): Promise<IBundleUploadResult[]> {
    if (items.length === 0) {
      return [];
    }

    const firstItem = items[0].tItem;

    // Bundling only admits small files that never chunk, so every bundled item is a whole
    // single-chunk blob → middleware uses S3 PutObject instead of multipart.
    const chunks = items.map((item) => ({
      object_type: 'btFILE',
      cachekey: item.tItem.cachekey || '',
      payload_crc32: item.crc32,
      enc_offset_byte: 0,
      payload_len: item.encryptedSize,
      enc_chunk_size_bytes: item.encryptedSize,
      is_single_chunk_blob: true,
    }));

    const jsonPayload = {
      device_sig_b64: firstItem.device_sig_b64,
      backup_id: firstItem.backup_id,
      device_id: firstItem.device_id,
      user_id: firstItem.user_id || this.userService.get('uid'),
      servtime: firstItem.servtime,
      chunks,
    };

    // Build FormData for each attempt (can't reuse FormData after send)
    const formData = new FormData();
    formData.append('json', JSON.stringify(jsonPayload));

    // Append payloads for each item
    items.forEach((item, idx) => {
      const preparedData = this.syncCryptService.prepareDataSend(item.encryptedData);
      const blob = new Blob([preparedData as unknown as BlobPart]);
      formData.append(`payload${idx}`, blob, `payload${idx}.data`);
    });

    // Cycle through upload hosts on retry (same pattern as uploadMultiChunk)
    const hostIndex = retryAttempt % this.urlService.uploadhosts.length;
    const url = this.urlService.mkUpload(
      this.urlService.uploadhosts[hostIndex],
      'uploadmultichunk',
      firstItem.storage_backend,
    );

    return new Promise((resolve, reject) => {
      this.http
        .post<any>(url, formData, { reportProgress: false, observe: 'response' })
        .pipe(
          timeout(MULTI_CHUNK_UPLOAD_TIMEOUT_MS),
          catchError((err) => {
            this.loggerService.error(
              `Bundled upload failed on host ${this.urlService.uploadhosts[hostIndex]}: ${JSON.stringify(err)}`,
            );
            return throwError({ errcode: 7000, originalError: err });
          }),
        )
        .subscribe(
          (response) => {
            const jsonData = response.body;

            // Handle retryable errors (same logic as uploadMultiChunk)
            if (jsonData && jsonData.success === 0 && jsonData.errcode === 2) {
              this.loggerService.warn('Bundled upload received errcode 2, retrying');
              this.retryBundledUpload(items, retryAttempt, resolve, reject, jsonData);
              return;
            }

            if (jsonData && jsonData.success === 0 && !jsonData.errcode) {
              // Check for CRC mismatch
              if (
                Array.isArray(jsonData.errors) &&
                jsonData.errors.some((error: string) => typeof error === 'string' && error.includes('crc_mismatch'))
              ) {
                this.loggerService.info('Bundled upload CRC mismatch detected, retrying');
                this.retryBundledUpload(items, retryAttempt, resolve, reject, jsonData);
                return;
              }
              this.loggerService.error('Bundled upload failed with success=0: ' + JSON.stringify(jsonData));
              reject({ errcode: 7020 });
              return;
            }

            if (jsonData && jsonData.success === 1 && jsonData.chunks && jsonData.chunks.length >= 1) {
              // Success - map results back to items
              const results: IBundleUploadResult[] = items.map((item, idx) => ({
                tItem: item.tItem,
                blob_id: jsonData.chunks[idx]?.blob_id,
                cachekey: jsonData.chunks[idx]?.cachekey,
                success: !!jsonData.chunks[idx]?.blob_id,
              }));
              resolve(results);
            } else {
              this.loggerService.error('Bundled upload returned unexpected response: ' + JSON.stringify(jsonData));
              reject({ errcode: 7020 });
            }
          },
          (err) => {
            // Handle HTTP errors - retry on 5xx or connection errors
            if (err.status === -1 || err.status >= 500 || err.status === 0) {
              this.retryBundledUpload(items, retryAttempt, resolve, reject, err);
              return;
            }
            this.loggerService.error('Bundled upload HTTP error: ' + JSON.stringify(err));
            reject(err);
          },
        );
    });
  }

  /**
   * Retry bundled upload with next host (same pattern as retryUpload for single chunks).
   */
  private retryBundledUpload(
    items: IBundleItem[],
    retryAttempt: number,
    resolve: (value: IBundleUploadResult[]) => void,
    reject: (reason: any) => void,
    errResponse: any,
  ): void {
    const nextRetryAttempt = retryAttempt + 1;
    const totalRetries = this.urlService.uploadhosts.length * MAX_RETRY_PER_UPLOAD_HOST;

    if (nextRetryAttempt < totalRetries) {
      const nextHost = this.urlService.uploadhosts[nextRetryAttempt % this.urlService.uploadhosts.length];
      this.loggerService.warn(`Retrying bundled upload with host ${nextHost}, retryCount: ${nextRetryAttempt}`);
      setTimeout(
        () => {
          this.uploadBundledChunks(items, nextRetryAttempt).then(resolve).catch(reject);
        },
        300 * (nextRetryAttempt + 1),
      );
    } else {
      this.loggerService.error(`Bundled upload failed all ${totalRetries} retry attempts`);
      this.loggerService.error(`errApiResponse: ${JSON.stringify(errResponse)}`);
      reject({ errcode: 7000 });
    }
  }

  /**
   * Uploads a given transfer Item.  The tItem is passed via reference
   * and modified within these methods.
   * @param tItem TransferItemUpload
   * @returns ITransferItemStats
   */
  public async uploadItem(tItem: ITransferItemUpload): Promise<ITransferItemStats> {
    const startMs = window.performance.now();
    // Reset all values since each uploadItem
    tItem.sha1_digest = this.syncDigestService.init();
    tItem.encTime = 0;
    tItem.digestTime = 0;
    tItem.appendTime = 0;
    tItem.sendTime = 0;
    tItem.readTime = 0;
    let preUploadTime = 0;
    let uploadTime = 0;
    let finishUploadTime = 0;
    let startUpload = 0;
    let startFinishUpload = 0;

    if (tItem.status === TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED) {
      return Promise.reject({ errcode: TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED });
    }
    tItem.status = TRANSFER_STATUS.STATUS_WORKING;
    const startPreUpload = window.performance.now();

    try {
      const item = await this.preUpload(tItem);
      preUploadTime = window.performance.now() - startPreUpload;
      startUpload = window.performance.now();
      const tItemUploaded = await this.getFile(item);
      this.loggerService.info('Encryption time = ' + tItem.encTime + ' ms');
      uploadTime = window.performance.now() - startUpload;

      /*
          unaccounted time is time spent doing the upload that is not
          already accounted for, usually that is just overhead
          within the code.  A high value here means there is an
          unoptimized piece of code running
      */
      const unaccountedTime = (
        uploadTime -
        tItem.sendTime -
        tItem.encTime -
        tItem.digestTime -
        tItem.appendTime -
        tItem.readTime
      ).toFixed(2);
      // prettier-ignore
      tItemUploaded.stats = [
        1,                          // version
        preUploadTime.toFixed(2),   // pathpreupload api call
        uploadTime.toFixed(2),      // total time to upload
        tItem.sendTime.toFixed(2),   // time on network
        tItem.encTime.toFixed(2),    // time encryption
        tItem.digestTime.toFixed(2), // time sha1 digest
        tItem.appendTime.toFixed(2), // time building payload
        tItem.readTime.toFixed(2),   // time reading fileobject
        unaccountedTime,            // unaccounted time
        tItemUploaded.filesize      // file size
      ].join(',');

      let syncId;

      startFinishUpload = window.performance.now();
      if (tItemUploaded.linkID) {
        const r = await this.finishUploadPublic(tItemUploaded);
        syncId = r.sync_id;
      } else {
        const r = await this.finishUpload(tItemUploaded);
        syncId = r.sync_id;
      }

      finishUploadTime = window.performance.now() - startFinishUpload;
      const timeTaken = window.performance.now() - startMs;
      const bps = tItem.filesize / (timeTaken / 1000);
      // prettier-ignore

      this.blendService.track(BlendEvent.UPLOAD, {
        totalTimeTakenInSeconds: +(timeTaken / 1000).toFixed(2),
        preUploadInMiliseconds: +preUploadTime.toFixed(2),
        uploadInMiliseconds: +uploadTime.toFixed(2),
        sendTimeInMiliseconds: +tItem.sendTime.toFixed(2),
        encryptTimeInMiliseconds: +tItem.encTime.toFixed(2),
        digestTimeInMiliseconds: +tItem.digestTime.toFixed(2),
        appendTimeInMiliseconds: +tItem.appendTime.toFixed(2),
        readTimeInMiliseconds: +tItem.readTime.toFixed(2),
        finishUploadInMiliseconds: +finishUploadTime.toFixed(2),
        unaccountedInMiliseconds: +unaccountedTime,
        fileSizeInBytes: +tItem.filesize,
        speedInKBPS: +(bps / 1024).toFixed(2),
      });

      this.loggerService.info(
        [
          ' Upload completed successfully for ',
          tItem.fileobj.name,
          ': ',
          (timeTaken / 1000).toFixed(2),
          'seconds total, ',
          ' pre-upload:',
          preUploadTime.toFixed(2),
          'ms, ',
          ' upload: ',
          uploadTime.toFixed(2),
          'ms ',
          ' sendTime: ',
          tItem.sendTime.toFixed(2),
          'ms ',
          ' encrypt time : ',
          tItem.encTime.toFixed(2),
          'ms ',
          ' digest time: ',
          tItem.digestTime.toFixed(2),
          'ms ',
          ' append time: ',
          tItem.appendTime.toFixed(2),
          'ms ',
          ' read time: ',
          tItem.readTime.toFixed(2),
          'ms ',
          ' finish-upload: ',
          finishUploadTime.toFixed(2),
          'ms ',
          ' unaccounted:',
          unaccountedTime,
          'ms ',
          ' file size: ',
          tItem.filesize,
          'bytes ',
          'at ',
          (bps / 1024).toFixed(2),
          ' kBps',
        ].join(' '),
      );
      tItem.progress_percent = 100;
      tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
      return {
        syncId,
        size: tItem.filesize,
        sendTime: tItem.sendTime,
        encTime: tItem.encTime,
        elapsed: timeTaken,
        elapsedPreUpload: preUploadTime,
        elapsedUpload: uploadTime,
        elapsedFinishUpload: finishUploadTime,
        bps: bps,
      };
    } catch (data) {
      this.loggerService.error('AN ERROR OCCURRED UPLOADING!!');
      if (typeof data === 'object' && data.errors && data.errors.length) {
        tItem.status = data.errors[0].error_code || TRANSFER_STATUS.STATUS_ERROR;
        this.loggerService.error(data.errors[0].error_msg);
      } else if (data.errcode) {
        tItem.status = data.errcode;
      } else if (data.code) {
        tItem.status = data.code;
      } else {
        tItem.status = TRANSFER_STATUS.STATUS_ERROR;
      }
      this.loggerService.error('TransferItem.status = ' + tItem.status);
      throw data;
    } finally {
      tItem.fileobjdata = undefined;
      tItem.sha1_digest = undefined;
    }
  }

  public async preUpload(tItem: ITransferItemUpload): Promise<ITransferItemUpload> {
    if (tItem.linkID) {
      try {
        const data = await this.apiService.execute<any>('pathpreuploadpublic', {
          sync_pid: tItem.sync_pid,
          enc_share_name: tItem.enc_share_name,
          publink_id: tItem.linkID,
        });
        tItem.user_id = data.user_id;
        tItem.sync_id = data.sync_id;
        tItem.servtime = data.servtime;
        tItem.backup_id = data.backup_id;
        tItem.device_id = data.device_id;
        tItem.device_sig_b64 = data.device_sig_b64;
        tItem.storage_backend = data.storage_backend || CONSTANTS.DEFAULT_STORAGE_BACKEND;
        return tItem;
      } catch (err) {
        this.loggerService.e(
          `Path preuploadpublic call failed for linkId ${tItem.linkID} sync_pid ${tItem.sync_pid}`,
          err,
        );
        throw new ErrCode(ERROR_CODES.FILE_UPLOAD_PREPARE_ERROR);
      }
    } else {
      try {
        const data = await this.apiService.execute<any>('pathpreupload', {
          sync_pid: tItem.sync_pid,
          enc_name: tItem.enc_name,
        });
        tItem.user_id = this.userService.get('uid');
        tItem.sync_id = data.sync_id;
        tItem.servtime = data.servtime;
        tItem.share_id = data.share_id;
        tItem.share_sequence = data.share_sequence;
        tItem.backup_id = data.backup_id;
        tItem.device_id = data.device_id;
        tItem.device_sig_b64 = data.device_sig_b64;
        tItem.enc_share_key = data.enc_share_key;
        tItem.share_key_id = data.share_key_id;
        tItem.storage_backend = data.storage_backend || CONSTANTS.DEFAULT_STORAGE_BACKEND;
        if (!tItem.share_key) {
          try {
            const sharekey = await this.syncCryptService.sharekeyDecrypt(data.enc_share_key, data.share_key_id);
            tItem.share_key = sharekey;
            return tItem;
          } catch (err) {
            this.loggerService.e(`Upload failed, could not decrypt share key ${data.share_key_id}`, err);
            throw err;
          }
        } else {
          return tItem;
        }
      } catch (err) {
        this.loggerService.e(`Path preupload call failed for sync_pid ${tItem.sync_pid}`, err);
        throw new ErrCode(ERROR_CODES.FILE_UPLOAD_PREPARE_ERROR);
      }
    }
  }

  /**
   * Wrapper function to get a file by using FileReader and reading in
   * chunks.  This calls function processPiece recursively until the
   * entire file is read, encrypted and uploaded
   *
   * getFileLarge will bypass loading the file at this point and instead
   * read in chunks via loadFileLarge.
   *
   * This optimization exists since small files fit in memory so we can
   * decrease the amount of times FileReader needs to get instantiated.
   * @param  {ITransferItemUpload} tItem The current TransferItem
   * @return {Promise}      Resolved/rejected when file is completely uploaded
   */
  private async getFile(tItem: ITransferItemUpload): Promise<ITransferItemUpload> {
    if (tItem.filesize >= MAX_SIZE_READ_MEMORY) {
      return await this.getFileLarge(tItem).toPromise();
    } else {
      return await this.getFileSmall(tItem).toPromise();
    }
  }

  private getFileLarge(tItem: ITransferItemUpload): Subject<ITransferItemUpload> {
    this.loggerService.info('FileUploader.getFileLarge(' + tItem.filesize + ')');
    const subject = new Subject<ITransferItemUpload>(),
      payload: IUploadPayload = {
        pieces: [],
        datalen: 0,
        data: [],
        offset: 0,
        chunklen: 0,
        enc_offset: 0,
      };
    this.runProcessPiece(tItem, payload, subject);
    return subject;
  }

  private getFileSmall(tItem: ITransferItemUpload): Subject<ITransferItemUpload> {
    this.loggerService.info('FileUploader.getFileSmall(' + tItem.filesize + ')');
    const subject = new Subject<ITransferItemUpload>(),
      payload: IUploadPayload = {
        pieces: [],
        datalen: 0,
        data: [],
        offset: 0,
        chunklen: 0,
        enc_offset: 0,
      };
    const start = window.performance.now();
    const fileReader = new FileReader();
    fileReader.onload = async (evt: any): Promise<void> => {
      const end = window.performance.now();
      tItem.readTime = tItem.readTime + (end - start);
      this.loggerService.info('FileReader resolved file ' + (end - start) + ' ms');
      tItem.fileobjdata = evt.target.result as ArrayBuffer;
      if (
        tItem.filesize != tItem.fileobj.size ||
        (tItem.fileobj.lastModified !== undefined && tItem.filedate != new Date(tItem.fileobj.lastModified).getTime())
      ) {
        this.loggerService.error('Source file has changed since being queued');
        subject.error({ errcode: 7012 });
        return;
      }
      this.runProcessPiece(tItem, payload, subject);
    };
    fileReader.onerror = (evt: any): void => {
      if (evt.target.error) {
        this.loggerService.error(evt.target.error.toString());
      }
      this.loggerService.error('Error reading input file');
      subject.error({ errcode: 7010 });
    };
    fileReader.readAsArrayBuffer(tItem.fileobj);
    return subject;
  }

  private runProcessPiece(
    tItem: ITransferItemUpload,
    payload: IUploadPayload,
    subject: Subject<ITransferItemUpload>,
  ): void {
    this.processPiece(tItem, payload, subject).catch((err: Error | ErrCode) => {
      this.loggerService.e('Failed to process upload piece', err);
      subject.error(this.toUploadError(err));
    });
  }

  private toUploadError(err: Error | ErrCode): Error | ErrCode {
    return err instanceof RangeError ? new ErrCode(ERROR_CODES.UPLOAD_OUT_OF_MEMORY) : err;
  }

  private async processPiece(
    tItem: ITransferItemUpload,
    payload: IUploadPayload,
    subject: Subject<ITransferItemUpload>,
  ): Promise<void | Subject<ITransferItemUpload>> {
    if (tItem.status === TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED) {
      subject.error({ errcode: TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED });
      return subject;
    }
    const queue = tItem.chunkqueue.shift();
    if (payload.pieces.length === 0) {
      payload.offset = queue.offset;
      payload.enc_offset = queue.enc_offset;
    }
    tItem.status = TRANSFER_STATUS.STATUS_ENC_UPLOAD;
    let buffer: ArrayBuffer;
    try {
      buffer = await this.loadPiece(tItem, queue.offset, queue.chunklen);
    } catch (ex) {
      if (ex instanceof RangeError) {
        throw ex;
      }
      this.loggerService.e('Failed to load piece', ex);
      return subject.error(new ErrCode(ERROR_CODES.FILE_UPLOAD_RETRIEVE_ERROR));
    }

    const bytearray = this.syncCryptService.arraybufferToBytes(buffer);

    const beginDigest = window.performance.now();
    tItem.sha1_digest = this.syncDigestService.update(tItem.sha1_digest, bytearray);
    tItem.digestTime += window.performance.now() - beginDigest;

    const beginFileDataEnc = window.performance.now();
    let encByteArray: ArrayLike<number>;
    try {
      encByteArray = await this.syncCryptService.filedataEncrypt(bytearray, tItem.data_key, queue.offset);
      this.updateProgress(TransferItemProgressType.CRYPT, tItem, encByteArray.length);
    } catch (ex) {
      if (ex instanceof RangeError) {
        throw ex;
      }
      throw new ErrCode(ERROR_CODES.FILE_DATA_DECRYPT_FAILED);
    }
    tItem.encTime += window.performance.now() - beginFileDataEnc;

    payload.pieces.push(encByteArray);
    payload.datalen += encByteArray.length;
    payload.chunklen += queue.chunklen;

    if (payload.chunklen >= CHUNKSIZE || !tItem.chunkqueue.length) {
      if (tItem.status === TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED) {
        subject.error({
          errcode: TRANSFER_STATUS.STATUS_UPLOAD_CANCELLED,
        });
        return subject;
      }
      this.loggerService.warn('Network transfer started');
      const startAppend = window.performance.now();
      payload.data = this.syncCryptService.filedataAppend(payload.pieces, payload.datalen);
      tItem.appendTime += window.performance.now() - startAppend;

      this.loggerService.info(`Payload contains ${payload.data.length} chunks`);

      tItem.status = TRANSFER_STATUS.STATUS_UPLOAD; // STATUS_UPLOAD

      const start = window.performance.now();
      this.sendChunk(tItem, payload).subscribe(
        (event: any) => {
          if (event.type === HttpEventType.Response) {
            const data = event.body;
            const end = window.performance.now();
            tItem.sendTime = tItem.sendTime + (end - start);
            tItem.blob_id = data.chunks[0].blob_id;
            tItem.cachekey = data.chunks[0].cachekey;
            payload.pieces = [];
            payload.datalen = 0;
            payload.data = [];
            payload.offset = 0;
            payload.chunklen = 0;
            payload.enc_offset = 0;
            if (tItem.chunkqueue.length) {
              this.runProcessPiece(tItem, payload, subject);
            } else {
              subject.next(tItem);
              subject.complete();
            }
          }
          if (event.type === HttpEventType.UploadProgress) {
            this.updateProgress(TransferItemProgressType.NETWORK, tItem, payload.offset + event.loaded);
          }
        },
        (err) => {
          subject.error(err);
        },
      );
    } else {
      if (tItem.chunkqueue.length) {
        this.runProcessPiece(tItem, payload, subject);
      } else {
        subject.next(tItem);
      }
    }
    return subject;
  }

  private updateProgress(type: TransferItemProgressType, tItem: ITransferItemUpload, bytes: number): void {
    switch (type) {
      case TransferItemProgressType.CRYPT:
        tItem.progress_crypt += bytes;
        break;
      case TransferItemProgressType.NETWORK:
        tItem.progress_network = bytes;
        break;
    }
    const progressBytes = tItem.progress_crypt + tItem.progress_network;
    const pct = Math.round((progressBytes / (tItem.filesize * 2)) * 100);
    tItem.progress_percent = pct >= 100 ? 99 : pct;
    if (
      tItem.status >= TRANSFER_STATUS.STATUS_WORKING &&
      tItem.status < 20 &&
      type === TransferItemProgressType.NETWORK
    ) {
      tItem.progress_bytes = progressBytes / 2;
    }
  }

  /**
   * Loads individual chunks.  For large files we return a portion of the file
   * via the FileReader, but if it's smaller we can just slice off the
   * arraybuffer
   * @param tItem TransferItem
   * @param offset byte offset
   * @param len  length to read
   * @returns ArrayBuffer
   */
  private loadPiece(tItem: ITransferItemUpload, offset: number, len: number): Promise<ArrayBuffer> {
    if (tItem.filesize >= MAX_SIZE_READ_MEMORY) {
      return this.loadPieceLarge(tItem, offset, len);
    } else {
      return this.loadPieceSmall(tItem, offset, len);
    }
  }

  private loadPieceLarge(tItem: ITransferItemUpload, offset: number, len: number): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const stop = len + offset || tItem.fileobj.size - 1;
      const fileReader = new FileReader();
      const start = window.performance.now();
      fileReader.onload = (evt: any): void => {
        const end = window.performance.now();
        tItem.readTime = tItem.readTime + (end - start);
        const bytesRead = (evt.target.result as ArrayBuffer).byteLength;
        if (bytesRead !== len) {
          this.loggerService.error(
            ['Attempting to read the file returned ', bytesRead, ' when we asked for ', len].join(''),
          );
          reject({ errcode: 7010 });
        } else {
          resolve(evt.target.result as ArrayBuffer);
        }
      };
      fileReader.onerror = (evt: any): void => {
        this.loggerService.error('Error reading file');
        reject({ errcode: 7010 });
      };
      fileReader.readAsArrayBuffer(tItem.fileobj.slice(offset, stop));
    });
  }

  private loadPieceSmall(tItem: ITransferItemUpload, offset: number, len: number): Promise<ArrayBuffer> {
    const stop = len + offset || tItem.fileobj.size - 1;

    return Promise.resolve(tItem.fileobjdata.slice(offset, stop));
  }

  // Utility function to calculate CRC32 and return it as a hexadecimal string
  private getCrc32Hex(payload: ArrayLike<number>): string {
    const uint8Array = new Uint8Array(payload); // Convert to Uint8Array
    const crc32result = crc32.buf(uint8Array); // Compute CRC32
    const unsignedCrc32Result = crc32result >>> 0; // Ensure it's an unsigned 32-bit integer
    return unsignedCrc32Result.toString(16).padStart(8, '0'); // Convert to hex string, padded to 8 digits
  }

  private sendChunk(tItem: ITransferItemUpload, payload: IUploadPayload): Subject<any> {
    const encChunkLen =
      payload.chunklen +
      Math.ceil(payload.chunklen / this.syncCryptService.GCM_PAYLOAD_SIZE) * GCM_CHUNK_OVERHEAD_BYTES;
    const crc32Hex = this.getCrc32Hex(payload.data); // Get CRC32 as a hexadecimal string

    // True when the whole file is sent in this single network request (first flush and no
    // further chunks queued), letting the middleware use S3 PutObject instead of a multipart upload.
    const isSingleChunkBlob = payload.enc_offset === 0 && !tItem.chunkqueue.length;

    return this.uploadMultiChunk(
      this.syncCryptService.prepareDataSend(payload.data),
      {
        cachekey: tItem.cachekey || '',
        enc_offset_byte: payload.enc_offset,
        enc_chunk_size_bytes: encChunkLen, // account for gcm tag
        payload_crc32: crc32Hex,
        payload_len: encChunkLen, // account for gcm tag
        is_single_chunk_blob: isSingleChunkBlob,

        // authentication data
        device_sig_b64: tItem.device_sig_b64,
        servtime: tItem.servtime,
        backup_id: tItem.backup_id,
        user_id: tItem.user_id || this.userService.get('uid'),
        device_id: tItem.device_id,
        storage_backend: tItem.storage_backend,
      },
      0,
    );
  }

  private uploadMultiChunk(payload: any, json: any, attempt = 0, sub?: Subject<any>): Subject<any> {
    const subject = sub || new Subject<any>();
    const formData = new FormData();
    const retryAttempt = attempt;
    let blob = new Blob([payload]);

    const startTime = window.performance.now();

    formData.append(
      'json',
      JSON.stringify({
        device_sig_b64: json.device_sig_b64,
        // used for auth
        backup_id: json.backup_id,
        device_id: json.device_id,
        user_id: json.user_id,
        servtime: json.servtime,
        chunks: [
          {
            object_type: 'btFILE',
            cachekey: json.cachekey || '',
            payload_crc32: json.payload_crc32,
            enc_offset_byte: json.enc_offset_byte,
            payload_len: json.payload_len,
            enc_chunk_size_bytes: json.enc_chunk_size_bytes,
            // Client-derived in sendChunk (or hardcoded true for bundles); never read back
            // from the server response, so it stays consistent across retries.
            is_single_chunk_blob: json.is_single_chunk_blob,
          },
        ],
      }),
    );
    formData.append('payload0', blob, 'payload0.data');

    if (blob.size !== json.payload_len) {
      this.loggerService.error(`Upload size mismatch. ${blob.size} != ${json.payload_len}`);
      subject.error({ code: 7011 });
      return subject;
    }

    const hostIndex = retryAttempt % this.urlService.uploadhosts.length;
    const url = this.urlService.mkUpload(
      this.urlService.uploadhosts[hostIndex],
      'uploadmultichunk',
      json.storage_backend,
    );

    const httpOptions = new Headers();
    httpOptions.append('Content-Type', undefined);

    this.http
      .post(url, formData, { reportProgress: true, observe: 'events' })
      .pipe(
        timeout(MULTI_CHUNK_UPLOAD_TIMEOUT_MS),
        catchError((err) => {
          if (err instanceof TimeoutError) {
            this.loggerService.error('upload timed out');
            this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
            this.loggerService.error('Upload timed out on first chunk');
            return throwError({ errcode: 7005 });
          } else {
            blob = null;
            this.loggerService.error(status + ' ' + url);
            this.loggerService.error('uploadChunk xhr onabort ' + JSON.stringify(err));
            return throwError({ errcode: 7000 });
          }
        }),
      )
      .subscribe(
        (response: any) => {
          const endTime = window.performance.now();
          this.loggerService.info('Upload transfer took ' + (endTime - startTime) + ' ms');
          const jsonData: any = response.body;
          // errcode 2 means the API thinks I should retry
          if (jsonData && jsonData.success == 0 && jsonData.errcode == 2) {
            if (jsonData.errors) {
              this.loggerService.error(JSON.stringify(jsonData.errors));
            }
            this.loggerService.warn('Error received errcode 2, retrying');
            this.retryUpload(payload, json, retryAttempt, subject, jsonData);
            return;
          } else if (jsonData && jsonData.success === 0 && !jsonData.errcode) {
            this.loggerService.error(response.status + ' ' + url);
            if (jsonData.errors) {
              this.loggerService.error(JSON.stringify(jsonData.errors));
              // Verify jsonData.errors is an array and contains a 'crc_mismatch' error string
              if (
                Array.isArray(jsonData.errors) &&
                jsonData.errors.some((error) => typeof error === 'string' && error.includes('crc_mismatch'))
              ) {
                this.loggerService.info('CRC mismatch detected. Retrying chunk upload...');
                this.retryUpload(payload, json, retryAttempt, subject, jsonData);
                return;
              }
            }
            this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
            this.loggerService.error(`OUTPUT: ${JSON.stringify(response)}`);
            if (jsonData.errors) {
              this.loggerService.error(JSON.stringify(jsonData.errors));
            }
            this.loggerService.error('uploadChunk success == 0, errcode = 7020 in upload chunk');
            return subject.error({ errcode: 7020 });
          } else if (jsonData && jsonData.success == 0) {
            this.loggerService.error(response.status + ' ' + url);
            this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
            this.loggerService.error(`OUTPUT: ${response}`);
            if (jsonData.errors) {
              this.loggerService.error(JSON.stringify(jsonData.errors));
            }
            this.loggerService.error('uploadChunk success == 0 error in upload chunk');
            return subject.error({ errcode: 7020 });
          } else {
            this.loggerService.info(status + ' ' + url + ' success');
            if (jsonData) {
              if (jsonData.success == 1 && jsonData.chunks.length >= 1) {
                // For single chunk uploads, return first chunk
                // Bundled uploads use uploadBundledChunks() which handles multiple chunks
                subject.next(jsonData.chunks[0]);
              } else {
                this.loggerService.error('An unexpected result was received during upload');
                this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
                this.loggerService.error(`OUTPUT: ${JSON.stringify(jsonData)}`);
                subject.error({ errcode: 7020 });
              }
            }
          }
          subject.next(response);
        },
        (resp) => {
          this.loggerService.error(`${resp.status} ${resp.xhrStatus} ${url}`);
          if (resp.status === 0 || !resp) {
            this.loggerService.error('uploadChunk status = 0 || !response ' + url);
            this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
            this.loggerService.error(`OUTPUT: ${JSON.stringify(resp)}`);
            subject.error({ errcode: 7023 });
            return;
          } else if (resp.status === -1 || resp.status >= 500) {
            this.retryUpload(payload, json, retryAttempt, subject, resp);
            return;
          } else {
            this.loggerService.error('An unknown error occurred');
            this.loggerService.error(`INPUT: ${JSON.stringify(json)}`);
            this.loggerService.error(`OUTPUT: ${JSON.stringify(resp)}`);
            subject.error({ errcode: 7000 });
          }
        },
      );
    return subject;
  }

  private retryUpload(payload: any, json: any, retryAttempt: number, subject: Subject<any>, errApiResponse: any): void {
    const nextRetryAttempt = retryAttempt + 1;
    const totalRetries = this.urlService.uploadhosts.length * MAX_RETRY_PER_UPLOAD_HOST;

    if (nextRetryAttempt < totalRetries) {
      this.loggerService.warn(
        `Retrying upload with host ${this.urlService.uploadhosts[nextRetryAttempt % this.urlService.uploadhosts.length]}, retryCount: ${nextRetryAttempt}`,
      );
      setTimeout(
        () => {
          this.uploadMultiChunk(payload, json, nextRetryAttempt, subject);
        },
        300 * (nextRetryAttempt + 1),
      );
    } else {
      this.loggerService.error(`Failed all ${totalRetries} retry attempts. INPUT: ${JSON.stringify(json)}`);
      this.loggerService.error(`errApiResponse OUTPUT: ${JSON.stringify(errApiResponse)}`);
      subject.error({ code: 7000 });
    }
  }

  /**
   Send job to setjob api. The job would be inserted in resque for thumbnail generation.
   */
  private async sendThumbnailJob(
    compatUrl: string,
    type: string,
    encDataKey: string,
    cacheKey: string,
    userId: number,
    deviceId: number,
    linkOwnerId: number,
  ): Promise<any> {
    return this.apiService
      .execute('setjob', {
        job_name: 'publink.ThumbnailGeneration',
        compat_url: compatUrl,
        type: type,
        enc_datakey: encDataKey,
        cachekey: cacheKey,
        user_id: userId,
        device_id: deviceId,
        link_owner_id: linkOwnerId,
      })
      .then((response) => {
        return response;
      });
  }

  private async getThumbnail(tItem: ITransferItemUpload): Promise<void> {
    let thumbnailProcessing = false;
    try {
      let mfsUrl: string,
        host = window.location.href;
      if (window.location.search) {
        host = host.replace(window.location.search, '');
      }

      const rsaDatakey = await this.syncCryptService.compatDatakeyEncrypt(
        this.syncCryptService.bytesToB64(tItem.data_key),
      );
      const encHost = await this.syncCryptService.compatDatakeyEncrypt(this.base64Service.encode(host));

      const pathListSubscription = this.linkPathListService.getSubscription().subscribe(async (data) => {
        if (data.loaded && data.sorted) {
          const uploadedItem = data.pathlist.find((val) => {
            if (val.enc_share_name === tItem.enc_share_name) {
              return true;
            }
          });

          if (uploadedItem !== undefined && !thumbnailProcessing) {
            thumbnailProcessing = true;
            tItem.link_owner_id = uploadedItem.link_owner_id;
            tItem.sync_id = uploadedItem.sync_id;
            let params = this.urlService.getDownloadPubLinkParams(tItem, rsaDatakey, encHost);
            // Password-locked links pass the passwordlock alongside `req` so
            // sync-core mints a pltoken. The thumbnail job fetches the URL
            // server-side (no cookie/header), so the token must ride in it.
            const signReq: any = { req: params };
            if (uploadedItem.linkpasswordlock) {
              signReq.passwordlock = uploadedItem.linkpasswordlock;
            }
            const result = await this.apiService.execute<any>('linksignrequest', signReq).catch((err) => {
              throw err;
            });
            params = result.response;

            this.syncCookieService.deleteCookie('passwordlock');
            this.syncCookieService.deleteCookie('thumbnail_sign');

            if (uploadedItem.linkpasswordlock) {
              this.syncCookieService.setDownloadPubLink(uploadedItem.linkpasswordlock);
            }
            mfsUrl = await this.urlService.internalDownloadPubLink(tItem, params, result.pltoken);
            const sendThumbnailJobResponse = await this.sendThumbnailJob(
              this.base64Service.encode(mfsUrl),
              tItem.filedata.mimetype,
              this.base64Service.encode(rsaDatakey.replace(/=/g, '')),
              tItem.cachekey,
              parseInt(uploadedItem.user_id, 10),
              data.cwd.event_device_id,
              tItem.link_owner_id,
            );

            if (pathListSubscription) {
              pathListSubscription.unsubscribe();
            }
          }
        }
      });
    } catch (err) {
      this.loggerService.error(`Error in getThumbnail for public uploads:: ${err}`);
    }
  }

  public async buildFinishBackupPublic(tItem: ITransferItemUpload): Promise<FinishBackupApiInput> {
    const dataKey = tItem.data_key,
      filedigest = this.syncDigestService.finish(tItem.sha1_digest),
      filedigestPromise = this.syncCryptService.filedigestEncrypt(filedigest, tItem.data_key),
      datakeyPromise = this.syncCryptService.datakeyEncrypt(this.syncCryptService.bytesToB64(dataKey), tItem.share_key);

    const result = await Promise.all([filedigestPromise, datakeyPromise]);
    const keys: DirtyOutEncResultSync[] = [
      {
        sync_id: tItem.sync_id || tItem.sync_pid,
        sharekey_id: tItem.share_key_id,
        enc_share_name: tItem.enc_share_name,
        servtime: tItem.servtime,
        enc_data_key: result[1],
      },
    ];
    return {
      cachekey: tItem.cachekey,
      publink_id: tItem.linkID,
      blob_id: tItem.blob_id,
      size_bytes: tItem.filesize,
      piece_size_bytes: tItem.filesize,
      file_digest: result[0],
      piece_digest: result[0],
      enc_piece_size_bytes: tItem.filesize + GCM_CHUNK_OVERHEAD_BYTES * tItem.chunkamt,
      share_id: tItem.share_id,
      share_sequence: tItem.share_sequence,
      keys: keys,
      sync_pid: tItem.sync_pid,
      user_id: tItem.user_id,
      device_id: tItem.device_id,
      enc_name: tItem.enc_name,
      stats: tItem.stats,
    };
  }

  public async finishUploadPublic(tItem: ITransferItemUpload): Promise<any> {
    const finishBackupPayload = await this.buildFinishBackupPublic(tItem);

    if (this.allowThumbnails(tItem)) {
      this.getThumbnail(tItem);
    }

    // backup_id, servtime, and device_sig_b64 (received from pathpreuploadpublic) are
    // required for AuthTypeWebMFS auth; user_id and device_id are already set on the
    // payload by buildFinishBackupPublic().
    const response = await this.finishBackupRequest(
      {
        ...finishBackupPayload,
        backup_id: tItem.backup_id,
        servtime: tItem.servtime,
        device_sig_b64: tItem.device_sig_b64,
      },
      tItem,
    );
    tItem.sha1_digest = undefined;
    return response;
  }

  private finishBackupRequest(payload: FinishBackupApiInput, tItem: ITransferItemUpload): Promise<any> {
    const queryParams = tItem.storage_backend ? '?storage_backend=' + tItem.storage_backend : '';
    return this.apiService.execute('webfinishbackup', payload, undefined, undefined, queryParams);
  }

  public async buildFinishBackup(tItem: ITransferItemUpload): Promise<FinishBackupApiInput> {
    const keysArray = await this.dirtyOutService.executeForSync(
      tItem.sync_pid,
      tItem.filename,
      this.syncCryptService.bytesToB64(tItem.data_key),
    );

    const digest = this.syncDigestService.finish(tItem.sha1_digest);
    const linkid = tItem.linkID || undefined;
    const encFileDigest = await this.syncCryptService.filedigestEncrypt(digest, tItem.data_key);

    const finishBackupPayload: FinishBackupApiInput = {
      cachekey: tItem.cachekey,
      publink_id: linkid,
      blob_id: tItem.blob_id,
      size_bytes: tItem.filesize,
      piece_size_bytes: tItem.filesize,
      file_digest: encFileDigest,
      piece_digest: encFileDigest,
      enc_piece_size_bytes: tItem.filesize + GCM_CHUNK_OVERHEAD_BYTES * tItem.chunkamt,
      share_id: tItem.share_id,
      share_sequence: tItem.share_sequence,
      keys: keysArray,
      sync_pid: tItem.sync_pid,
      enc_name: tItem.enc_name,
      stats: tItem.stats,
    };
    if (this.allowThumbnails(tItem)) {
      this.syncCookieService.deleteCookie('thumbnail_sign');
      const ts = Date.now();

      const rsaDatakey = await this.syncCryptService.compatDatakeyEncrypt(
        this.syncCryptService.bytesToB64(tItem.data_key),
      );

      let mfsUrl = await this.urlService.internalDownloadMfsUrl(tItem, rsaDatakey, ts);
      const input = new BaseApiInput();
      input.servtime = ts;
      const defaults = await this.syncCryptService.signApiReq(input).catch((err) => {
        throw err;
      });
      mfsUrl = mfsUrl + '&access_token=' + defaults.access_token;
      finishBackupPayload.compat_url = this.base64Service.encode(mfsUrl);
      finishBackupPayload.type = tItem.filedata.mimetype;
      finishBackupPayload.enc_datakey = this.base64Service.encode(rsaDatakey.replace(/=/g, ''));
      this.syncCookieService.setThumbnailSignature(defaults.signature);
    }
    return finishBackupPayload;
  }

  public async finishUpload(tItem: ITransferItemUpload): Promise<any> {
    const finishBackupPayload: FinishBackupApiInput = await this.buildFinishBackup(tItem);
    const response = await this.finishBackupRequest(finishBackupPayload, tItem);
    tItem.sha1_digest = undefined;
    return response;
  }

  private allowThumbnails(tItem: ITransferItemUpload): boolean {
    if (this.userService.hasFeatureFlag(FeatureFlags.DISABLE_WEB_THUMBNAILS)) {
      return false;
    }
    if (!this.userService.isThumbnailsAllowed() || !this.isImage(tItem)) {
      return false;
    }
    if (tItem.filedata.mimetype === 'image/heic') {
      return tItem.fileobj.size <= MAX_SIZE_HEIC_THUMB;
    }
    return tItem.fileobj.size <= MAX_SIZE_IMG_THUMB;
  }

  private isImage(item: ITransferItemUpload): boolean {
    if (!item || !item.filename) {
      return false;
    }
    const name = item.filename;
    const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
    let canPreview = false;
    switch (ext) {
      case 'jpg':
      case 'png':
      case 'jpeg':
      case 'gif':
      case 'heic':
      case 'webp':
      case 'avif':
      case 'tiff':
        canPreview = true;
        break;
      default:
        canPreview = false;
    }
    return canPreview && ext != name.toLowerCase();
  }
}
